import { format } from 'date-fns';
import { captureException } from '@sentry/react';
import { GraphQLResult } from '@aws-amplify/api-graphql';
import { createAsyncThunk } from '@reduxjs/toolkit';
import { graphqlOperation } from 'aws-amplify';
import { dispatchMessage } from 'graphql/queries';
import {
  BudgetSpendByMonthAndPaymentCard,
  CardGroup,
  DispatchMessageQuery,
  DispatchMessageQueryVariables,
  GetBudgetSpendByMonthAndPaymentCardQuery,
  GetBudgetSpendByMonthAndPaymentCardQueryVariables,
  HighnotePaymentCard,
  HNActivatePaymentCardInput,
  HNClientToken,
  HNGeneratePaymentCardClientTokenInput,
  HNPaymentCard,
  HNPaymentCardStatus,
  HNSuspendPaymentCardInput,
  InviteUserToPaidol,
  IssuePaymentCardMutationVariables,
  ListHighnotePaymentCardsByPaidolIdQuery,
  UpdateHighnotePaymentCardInput,
  UpdateHighnotePaymentCardMutation,
} from 'API';
import type {
  GetCardsInput,
  GetCardsOrConditionFilterInput,
  GetCardsPayload,
  GetCardsPayloadResult,
  MutationNotifyCardCanceledArgs,
} from 'CDK_API';
// SEE-1232 row f: GetHighnotePaymentCard, GetPaymentCardLocalDetails and CreateHighnotePaymentCard
// now read/write CDK (cardService); the Amplify documents stay in ./queries (SEE-1238 removes them).
import {
  HnGetPaymentCardsForBusinessIdQuery,
  ListHighnotePaymentCards,
  UpdateHighnotePaymentCard,
} from 'app/pages/store/queries';
import { graphqlRequest } from 'app/services/middleware';
import { highnoteService } from 'app/services/highnote.service';
import { cardService } from 'app/services/card.service';

/* PaymentCardSlice */
export type PaymentCardDetails = {
  paymentCardDetails?: HNPaymentCard;
  highnotePaymentCard?: HighnotePaymentCard;
  highnotePaymentCardBudget?: BudgetSpendByMonthAndPaymentCard;
  budgetSpendByCurrentCycle?: {
    amount: number;
    transactionCount: number;
    processedTransactionIds?: string[];
  };
};
export interface GetHighnotePaymentCardResults {
  hnGetPaymentCardDetails: HNPaymentCard;
  getHighnotePaymentCard?: HighnotePaymentCard;
  getBudgetSpendByMonthAndPaymentCard?: BudgetSpendByMonthAndPaymentCard;
  getBudgetSpendByCurrentCycle?: {
    amount: number;
    transactionCount: number;
    processedTransactionIds?: string[];
  };
}

export interface GetPaymentCardDetailsArgs {
  id: string;
  paidolId: string;
  yearAndMonth: string;
}

export interface GetPaymentCardsArgs {
  id: string;
  paidolId: string;
  status?: string;
  after?: string;
}

export interface GetHighnotePaymentCardArgs {
  paymentCardId: string;
  paidolId: string;
  yearAndMonth: string;
}

interface AssignPaymentCardArgs {
  input: InviteUserToPaidol;
  highnotePaymentCardId: string;
}

export interface FetchGroupCardsArgs {
  searchTerm: string;
  paidolId: string;
  excludeGroupedCards?: boolean;
}

//Issue payment card
export const issuePaymentCardForFinancialAccount = createAsyncThunk(
  'cards/card/issuePaymentCard',
  async ({ input }: IssuePaymentCardMutationVariables, { rejectWithValue, dispatch }) => {
    try {
      const responseData = await cardService.issuePaymentCard({ input });

      // Check if the backend returned an error in the response body
      if (responseData?.errorMessage) {
        return rejectWithValue({
          errors: [
            {
              message: responseData.errorMessage,
              errorType: responseData.errorType,
            },
          ],
          // Propagate paymentCardId when a card was created but physical order failed,
          // so the caller can retry with ORDER_PHYSICAL instead of issuing a new card.
          ...(responseData.paymentCardId && { paymentCardId: responseData.paymentCardId }),
        });
      }

      // Only refresh card list on success
      if (input.formType === 'UPDATE_CARD' && input.selectedPaymentCardId) {
        dispatch(
          getCard({
            filter: {
              and: [{ or: [{ paymentCardId: { eq: input.selectedPaymentCardId } }] }],
              paidolId: { eq: input.paidolId },
            },
          })
        );
      } else {
        dispatch(getCardList({ filter: { paidolId: { eq: input.paidolId } } }));
      }

      return {
        result: responseData,
        formType: input.formType,
        paymentCardId: input.selectedPaymentCardId,
        paidolId: input.paidolId,
      };
    } catch (error: unknown) {
      return rejectWithValue(error);
    }
  }
);

export const getPaymentCardDetails = createAsyncThunk(
  'cards/paymentCards/getPaymentCardDetails',
  async ({ id, paidolId, yearAndMonth }: GetPaymentCardDetailsArgs) => {
    // Both halves are CDK AppSync now: v2Highnote for hn* data, v2Card for the local record
    // (SEE-1232 row f — the same batched GetPaymentCardLocalDetails request).
    const [hnResult, localResult] = await Promise.all([
      highnoteService.hnGetPaymentCardDetails({ id }),
      cardService.getPaymentCardLocalDetails<HighnotePaymentCard, BudgetSpendByMonthAndPaymentCard>({
        paymentCardId: id,
        paidolId,
        yearAndMonth,
      }),
    ]);

    const response: PaymentCardDetails = {
      paymentCardDetails: hnResult as HNPaymentCard,
      highnotePaymentCard: localResult?.getHighnotePaymentCard ?? undefined,
      highnotePaymentCardBudget: localResult?.getBudgetSpendByMonthAndPaymentCard ?? undefined,
    };
    return response;
  }
);

/* Associated with AssignCard/Table. OBSOLETE */
export const getPaymentCards = createAsyncThunk(
  'cards/paymentCards/getPaymentCards',
  async ({ id, paidolId, status = undefined, after = undefined }: GetPaymentCardsArgs, { dispatch }) => {
    const input = {
      id,
      first: 20,
      after,
      ...(status && {
        filterBy: {
          status: { equals: status },
        },
      }),
    };

    const result = (await highnoteService.hnGetPaymentCardsForBusinessId(input)) as
      | HnGetPaymentCardsForBusinessIdQuery['hnGetPaymentCardsForBusinessId']
      | undefined;

    const paymentCards = result?.paymentCards;

    if (paymentCards?.edges) {
      paymentCards.edges.forEach((card) => {
        if (card.node) {
          dispatch(
            getPaymentCardDetails({
              id: card.node.id,
              paidolId,
              yearAndMonth: format(new Date(), 'yyyy-MM'),
            })
          );
        }
      });
    }

    return paymentCards;
  }
);

export const getCardList = createAsyncThunk('cards/paymentCards/getCards', async (input: GetCardsInput) => {
  return cardService.getCards({ input }) as Promise<GetCardsPayloadResult>;
});

export const getCard = createAsyncThunk(
  'cards/paymentCards/getCard',
  async (input: GetCardsInput, { dispatch }) => {
    return cardService.getCards({ input }).then((result) => {
      const card = result?.items?.at(0) as GetCardsPayload | undefined;
      const { paymentCardId, paidolId } = card ?? {};

      if (paymentCardId && paidolId) {
        dispatch(
          getHighnotePaymentCard({ paymentCardId, paidolId, yearAndMonth: format(new Date(), 'yyyy-MM') })
        );
      }

      return card;
    });
  }
);

export const getCardDetails = createAsyncThunk(
  'cards/paymentCards/getCardDetails',
  async (paymentCardId: string) => {
    // SEE-1232 row f: transport flip only — same argument, same selection, same record.
    return (await cardService.getHighnotePaymentCard<HighnotePaymentCard>(
      paymentCardId
    )) as HighnotePaymentCard;
  }
);

export const getCardholderNames = createAsyncThunk(
  'cards/paymentCards/getCardholderNames',
  async (paidolId: string, { dispatch }) => {
    return (
      graphqlRequest(graphqlOperation(ListHighnotePaymentCards, { paidolId }), dispatch) as Promise<
        GraphQLResult<ListHighnotePaymentCardsByPaidolIdQuery>
      >
    ).then((result) => result.data?.listHighnotePaymentCardsByPaidolId?.items as Array<HighnotePaymentCard>);
  }
);

/* SpecificCardSlice */
export const generatePaymentCardClientToken = createAsyncThunk(
  'cards/card/generatePaymentCardClient',
  async ({ paymentCardId, permissions }: HNGeneratePaymentCardClientTokenInput) => {
    const result = (await highnoteService.hnGeneratePaymentCardClientToken({
      paymentCardId,
      permissions,
    })) as HNClientToken;
    return result?.value;
  }
);

export const dispatchSpeedchatMessage = createAsyncThunk(
  'cards/card/dispatchSpeedchatMessage',
  async ({ message, userId, paidolId }: DispatchMessageQueryVariables, { dispatch }) => {
    return graphqlRequest(
      graphqlOperation(dispatchMessage, { userId, message, paidolId }),
      dispatch
    ) as Promise<GraphQLResult<DispatchMessageQuery>>;
  }
);

// SEE-186: the cancel-card email, composed server-side by v2-highnote. Replaces
// dispatchEmail, which sent caller-built to/subject/body through Amplify sendEmail.
export const notifyCardCanceled = createAsyncThunk(
  'cards/card/notifyCardCanceled',
  async ({ paymentCardId }: MutationNotifyCardCanceledArgs) => {
    return highnoteService.notifyCardCanceled({ paymentCardId });
  }
);

export const updateHighnotePaymentCard = createAsyncThunk(
  'cards/card/updateHighnotePaymentCard',
  async (input: UpdateHighnotePaymentCardInput, { dispatch }) => {
    return (
      graphqlRequest(graphqlOperation(UpdateHighnotePaymentCard, { input }), dispatch) as Promise<
        GraphQLResult<UpdateHighnotePaymentCardMutation>
      >
    ).then((result) => result?.data?.updateHighnotePaymentCard as HighnotePaymentCard);
  }
);

export const updateCardStatus = createAsyncThunk(
  'cards/card/updateCardStatus',
  async (input: UpdateHighnotePaymentCardInput, { dispatch }) => {
    return (
      graphqlRequest(graphqlOperation(UpdateHighnotePaymentCard, { input }), dispatch) as Promise<
        GraphQLResult<UpdateHighnotePaymentCardMutation>
      >
    ).then((result) => result?.data?.updateHighnotePaymentCard as HighnotePaymentCard);
  }
);

export const updateCardGroup = createAsyncThunk(
  'cards/card/updateCardGroup',
  async (
    {
      paymentCardId,
      cardGroupId,
      group,
    }: { paymentCardId: string; cardGroupId: string; group?: { id: string; name: string } | null },
    { dispatch }
  ) => {
    const input: UpdateHighnotePaymentCardInput = { paymentCardId, cardGroupId };

    const result = await (graphqlRequest(
      graphqlOperation(UpdateHighnotePaymentCard, { input }),
      dispatch
    ) as Promise<GraphQLResult<UpdateHighnotePaymentCardMutation>>);

    const paymentCard = result?.data?.updateHighnotePaymentCard as HighnotePaymentCard;

    // Return both the payment card and the group data for the Redux slice
    return { paymentCard, paymentCardId, group };
  }
);

export const activatePaymentCard = createAsyncThunk(
  'cards/card/activatePaymentCard',
  async ({ paymentCardId }: HNActivatePaymentCardInput, { dispatch }) => {
    const paymentCard = (await highnoteService.hnActivatePaymentCard({
      paymentCardId,
    })) as HNPaymentCard;

    if (paymentCard.status && paymentCard.status === HNPaymentCardStatus.ACTIVE) {
      dispatch(updateHighnotePaymentCard({ paymentCardId, status: paymentCard.status }));
    }

    return paymentCard;
  }
);

export const suspendPaymentCard = createAsyncThunk(
  'cards/card/suspendPaymentCard',
  async ({ paymentCardId }: HNSuspendPaymentCardInput) => {
    return cardService
      .lockUnlockPaymentCard({ input: { paymentCardId, action: 'lock' } })
      .then((response) => {
        return response
          ? {
              id: response.paymentCardId,
              status: response.status as HNPaymentCardStatus,
            }
          : null;
      });
  }
);

export const unlockPaymentCard = createAsyncThunk(
  'cards/card/unlockPaymentCard',
  async ({ paymentCardId }: { paymentCardId: string }) => {
    return cardService
      .lockUnlockPaymentCard({ input: { paymentCardId, action: 'unlock' } })
      .then((response) => {
        return response
          ? {
              id: response.paymentCardId,
              status: response.status as HNPaymentCardStatus,
            }
          : null;
      });
  }
);

export const closePaymentCard = createAsyncThunk(
  'cards/card/closePaymentCard',
  async ({ paymentCardId }: HNActivatePaymentCardInput) => {
    const paymentCard = (await highnoteService.hnClosePaymentCard({
      paymentCardId,
    })) as HNPaymentCard;

    return paymentCard;
  }
);

export const getHighnotePaymentCard = createAsyncThunk<PaymentCardDetails, GetHighnotePaymentCardArgs>(
  'cards/card/getHighnotePaymentCard',
  async ({ paymentCardId, paidolId, yearAndMonth }: GetHighnotePaymentCardArgs) => {
    // Three CDK sources: v2Highnote for hn* card data, v2Card for the local record + month
    // budget (SEE-1232 row f — the same batched GetPaymentCardLocalDetails request, formerly
    // Amplify @model), and v2Card for the current-cycle budget spend (SEE-307).
    const [hnResult, localResult, budgetSpendByCurrentCycle] = await Promise.all([
      highnoteService.hnGetPaymentCardDetails({ id: paymentCardId }),
      cardService.getPaymentCardLocalDetails<HighnotePaymentCard, BudgetSpendByMonthAndPaymentCard>({
        paymentCardId,
        paidolId,
        yearAndMonth,
      }),
      // SEE-307: degrade a budget-spend failure to `undefined` rather than failing the whole
      // card-details load. On develop this value came back as a nested field inside the batched
      // local-details query, so a failure just nulled the field. The migration split it into its
      // own call behind `assert_paidol_member`; without this catch an all-or-nothing Promise.all
      // would blow up the entire detail view on any error from this one call.
      cardService.getBudgetSpendByCurrentCycle({ paymentCardId }).catch(() => undefined),
    ]);

    const response: PaymentCardDetails = {
      paymentCardDetails: hnResult as HNPaymentCard,
      highnotePaymentCard: localResult?.getHighnotePaymentCard ?? undefined,
      highnotePaymentCardBudget: localResult?.getBudgetSpendByMonthAndPaymentCard ?? undefined,
      // Normalize the CDK payload (nullable amount/transactionCount per schema) into the
      // non-null shape the UI consumes. Under the old Amplify `any`-typed path these nulls
      // were silently coerced; defaulting to 0 is the same "no spend" meaning, now type-safe.
      budgetSpendByCurrentCycle: budgetSpendByCurrentCycle
        ? {
            amount: budgetSpendByCurrentCycle.amount ?? 0,
            transactionCount: budgetSpendByCurrentCycle.transactionCount ?? 0,
            processedTransactionIds:
              budgetSpendByCurrentCycle.processedTransactionIds?.filter((id): id is string => id != null) ??
              undefined,
          }
        : undefined,
    };

    if (response.highnotePaymentCard) {
      return response;
    }

    // SEE-1232 row f: backfill through CDK. authPaidolId is derived server-side. CDK also checks
    // with Highnote that the card belongs to this company, so a denied create is likelier than on
    // Amplify. A failure is reported and leaves highnotePaymentCard undefined, so the Highnote card
    // details that already loaded still render instead of the whole load rejecting.
    try {
      const created = await cardService.createHighnotePaymentCard<HighnotePaymentCard>({
        paymentCardId,
        paidolId,
      });
      response.highnotePaymentCard = created ?? undefined;
    } catch (error) {
      captureException(error);
    }

    return response;
  }
);

export const assignPaymentCard = createAsyncThunk(
  'cards/paymentCards/assignPaymentCard',
  async ({ input, highnotePaymentCardId }: AssignPaymentCardArgs) => {
    let hostname = window.location.origin;
    if (!window.location.origin) {
      hostname =
        window.location.protocol +
        '//' +
        window.location.hostname +
        (window.location.port ? ':' + window.location.port : '');
    }
    return cardService.assignCard({ input, highnotePaymentCardId, hostname });
  }
);

export const budgetSpendByMonthAndPaymentCard = createAsyncThunk(
  'cards/card/budgetSpendByMonthAndPaymentCard',
  async (
    { paidolId, yearAndMonth, paymentCardId }: GetBudgetSpendByMonthAndPaymentCardQueryVariables,
    { rejectWithValue }
  ) => {
    try {
      // SEE-1232 row h: transport flip only — same arguments, same resolved record.
      return await cardService.getBudgetSpendByMonthAndPaymentCard<
        GetBudgetSpendByMonthAndPaymentCardQuery['getBudgetSpendByMonthAndPaymentCard']
      >({ paidolId, yearAndMonth, paymentCardId });
    } catch (error: unknown) {
      return rejectWithValue(error);
    }
  }
);

export const getCardWithFilters = createAsyncThunk(
  'cards/getCardWithFilters',
  async (input: GetCardsInput) => {
    return cardService.getCards({ input }) as Promise<GetCardsPayloadResult>;
  }
);

// Async thunk for fetching group cards
export const fetchGroupCards = createAsyncThunk(
  'cards/cardData/fetchGroupCards',
  async (
    { paidolId, cardGroup, nextToken }: { paidolId: string; cardGroup: CardGroup; nextToken?: string },
    { dispatch }
  ) => {
    const input: GetCardsInput = {
      filter: {
        paidolId: {
          eq: paidolId,
        },
        and: [
          {
            or: [
              {
                cardGroupId: {
                  eq: cardGroup.id,
                },
              },
            ],
          },
        ],
      },
      nextToken,
    };
    return cardService.getCards({ input }) as Promise<GetCardsPayloadResult>;
  }
);

// Async thunk for loading more group cards (pagination)
export const loadMoreGroupCards = createAsyncThunk(
  'cards/cardData/loadMoreGroupCards',
  async (
    { paidolId, cardGroup, nextToken }: { paidolId: string; cardGroup: CardGroup; nextToken: string },
    { dispatch }
  ) => {
    const input: GetCardsInput = {
      filter: {
        paidolId: {
          eq: paidolId,
        },
        and: [
          {
            or: [
              {
                cardGroupId: {
                  eq: cardGroup.id,
                },
              },
            ],
          },
        ],
      },
      nextToken,
    };
    return cardService.getCards({ input }) as Promise<GetCardsPayloadResult>;
  }
);

export const fetchAssinableCards = createAsyncThunk(
  'cards/cardData/fetchAssinableCards',
  async ({ paidolId, searchTerm, excludeGroupedCards = true }: FetchGroupCardsArgs, { dispatch }) => {
    const and: GetCardsOrConditionFilterInput[] = [
      {
        or: [
          {
            status: {
              ne: 'CLOSED',
            },
          },
        ],
      },
    ];

    // Only exclude grouped cards if excludeGroupedCards is true (default behavior for backward compatibility)
    if (excludeGroupedCards) {
      and.push({
        // Only cards with no group (cardGroupId is null, doesn't exist, or is NO_GROUP)
        or: [
          {
            cardGroupId: {
              exists: false,
            },
          },
        ],
      });
    }
    const input: GetCardsInput = {
      filter: {
        paidolId: {
          eq: paidolId,
        },
        and,
      },
      limit: 1000, // Fetch all assignable cards at once (no pagination)
    };

    // Handle search term - trim and check if it's not empty
    const trimmedSearchTerm = searchTerm?.trim() || '';

    if (trimmedSearchTerm) {
      // Search by last4 if search term contains digits
      // Note: cardholderName cannot be searched via wildcard in backend (maps to cardholderId)
      // so cardholder name filtering is done client-side
      if (trimmedSearchTerm.match(/[0-9]/)) {
        and.push({
          or: [
            {
              last4: {
                wildcard: `*${trimmedSearchTerm}*`,
              },
            },
          ],
        });
      }
      // Search terms with only letters: no backend filter applied (cardholder name filtering done client-side)
      // Search terms with digits: backend searches by last4
    }

    return cardService.getCards({ input }) as Promise<GetCardsPayloadResult>;
  }
);

export const fetchAssignableCardsLite = createAsyncThunk(
  'cards/cardData/fetchAssignableCardsLite',
  async ({ paidolId, searchTerm, excludeGroupedCards = true }: FetchGroupCardsArgs, { dispatch }) => {
    const and: GetCardsOrConditionFilterInput[] = [
      {
        or: [
          {
            status: {
              ne: 'CLOSED',
            },
          },
        ],
      },
    ];

    if (excludeGroupedCards) {
      and.push({
        or: [
          {
            cardGroupId: {
              exists: false,
            },
          },
        ],
      });
    }
    const input: GetCardsInput = {
      filter: {
        paidolId: {
          eq: paidolId,
        },
        and,
      },
      limit: 1000,
    };

    const trimmedSearchTerm = searchTerm?.trim() || '';

    if (trimmedSearchTerm) {
      if (trimmedSearchTerm.match(/[0-9]/)) {
        and.push({
          or: [
            {
              last4: {
                wildcard: `*${trimmedSearchTerm}*`,
              },
            },
          ],
        });
      }
    }

    // Cast lite response to GetCardsPayloadResult — lite fields are a subset,
    // missing fields will be undefined which is safe for SpendRulesForm usage
    return cardService.getCardsLite({ input }) as unknown as Promise<GetCardsPayloadResult>;
  }
);

export const fetchCardsByPaymentCardIds = createAsyncThunk(
  'cards/cardData/fetchCardsByPaymentCardIds',
  async ({ input }: { input: GetCardsInput }) => {
    return cardService.getCards({ input }) as Promise<GetCardsPayloadResult>;
  }
);
